import { useSnackbar } from 'notistack';
import {
    ExtendedRuleDefinition,
    FactType,
    Participant,
    Program,
    RetailerOptConfig,
    Rule,
    RetailerOptConfigStatusEnum,
} from '../../gen/api/api';
import useFieldHelpers from '../../helpers/fieldHelper';
import { appHistory } from '../../router/RouterComponent';
import ApiService from '../../services/ApiService';
import { DOT_REPLACEMENT_REGEX, FACT_INPUT_NAME_REGEX } from '../../shared/constants/RegexConstants';
import {
    ENDDATETIME_FIELD_AFTER_STARTDATE_MESSAGE,
    FIELD_REQUIRED_MESSAGE,
} from '../../shared/constants/ValidationMessagesConstants';
import { EARN_AND_BURN_ONLY_FACTS } from '../../shared/constants/EarnAndBurnConstants';
import startDateTimeBeforeEndDateTime from '../../shared/validators/EndDateTimeValidator';
import { ConfigurationFormInputs, RetailerOptConfigRich } from './ConfigurationFormSchema';
import { getGlobal } from 'reactn';
import { CAN_EDIT } from '../../shared/constants/AuthConstants';

const useConfigurationForm = () => {
    const { enqueueSnackbar } = useSnackbar();

    const { generateFieldName } = useFieldHelpers();

    const fetchConfiguration = async (
        configurationId: string,
        ruleDefinitions: ExtendedRuleDefinition[],
        setSelectedConfiguration: (configuration: RetailerOptConfigRich) => void,
    ) => {
        if (configurationId) {
            const configuration: RetailerOptConfig = (await ApiService().fetchConfigurationById(configurationId)).data;

            let program = null;
            let retailer = null;

            try {
                program = (await ApiService().fetchProgramById(configuration.programId)).data;
            } catch (e) {
                enqueueSnackbar('Unable to load selected program data.', { variant: 'error' });
            }

            try {
                retailer = (await ApiService().fetchRetailerById(configuration.retailerId)).data;
            } catch (e) {
                enqueueSnackbar('Unable to load selected participant data.', { variant: 'error' });
            }

            const configurationRich: RetailerOptConfigRich = {
                ...configuration,
                retailer: retailer,
                program: program,
                ruleInputs: generateFactInputsTemplate(ruleDefinitions),
            };

            setSelectedConfiguration(configurationRich);
        }
    };

    const extractRulesFilled = (values: { [key: string]: any }) => {
        return Object.keys(values)
            .filter((prop) => prop.match(FACT_INPUT_NAME_REGEX))
            .reduce((rules: { [key: string]: { [key: string]: any } }, prop) => {
                const match = prop.match(FACT_INPUT_NAME_REGEX);
                if (match && match[1] && match[2]) {
                    if (!rules[match[1].replace(DOT_REPLACEMENT_REGEX, '.')]) {
                        rules[match[1].replace(DOT_REPLACEMENT_REGEX, '.')] = {};
                    }

                    let value = values[prop];

                    // TODO
                    // If we get array of objects we map it to array of ids because that's what we want to save. This works because the 
                    // only case the preconditions match is when we get values from the ApiAutocomplete component in the multiple=true 
                    // variant (a.k.a. multiselect). Still much better solution would be to configure some map method in the FactConfig 
                    // and call it here.
                    if (Array.isArray(value) && value.reduce((acc, value) => acc && "id" in value, true)) {
                        value = values[prop].map((item: { id: number }) => item.id);
                    }

                    rules[match[1].replace(DOT_REPLACEMENT_REGEX, '.')][match[2].replace(DOT_REPLACEMENT_REGEX, '.')] =
                        value;
                }
                return rules;
            }, {});
    };

    /**
		Checks if the fact defined by it's fact code is a related to EARN group of facts
		Returns true if the fact is related to the EARN group of facts, otherwise returns false
	 */
    const isEarnOnlyAndNotEarnAndBurnFact = (factCode: string) => {
        return factCode.startsWith('earn') && !EARN_AND_BURN_ONLY_FACTS.has(factCode);
    };

    /**
		Returns true if the fact should be persisted by adding the fact value into the API request.
		If the fact should not be persisted, returns fals, and the fact is not added into the API request.
	 */
    const shouldSaveFact = (
        ruleCode: string,
        factCode: string,
        rulesFilled: { [key: string]: { [key: string]: any } },
    ) => {
        if (ruleCode === 'optin.rules.earnburn') {
            const earnOrBurnValue: string = rulesFilled['optin.rules.earnburn']['earnOrBurn'];
            if (earnOrBurnValue === 'earn-only' && factCode.startsWith('burn')) {
                return false;
            } else if (earnOrBurnValue === 'burn-only' && isEarnOnlyAndNotEarnAndBurnFact(factCode)) {
                return false;
            }
        }
        return true;
    };

    const constructRules = (
        rulesFilled: { [key: string]: { [key: string]: any } },
        ruleDefinitions?: ExtendedRuleDefinition[],
        rulesOriginal?: Rule[],
    ) => {
        const rulesConstructed: Array<Rule> = [];

        Object.keys(rulesFilled).forEach((ruleCode) => {
            const newRule: Rule = {
                ruleCode: ruleCode,
                ruleVersion: rulesOriginal?.find((rule) => rule.ruleCode === ruleCode)?.ruleVersion || '1',
                factValues: [],
            };
            Object.keys(rulesFilled[ruleCode])
                .filter((factCode) => shouldSaveFact(ruleCode, factCode, rulesFilled))
                .forEach((factCode) => {
                    newRule.factValues.push({
                        factCode: factCode,
                        factType:
                            ruleDefinitions?.find((ruleDefinition) => ruleDefinition.ruleCode === ruleCode)
                                ?.factDefinitions[factCode].factType || FactType.ObjectValue,
                        // @ts-ignore
                        value: rulesFilled[ruleCode][factCode],
                    });
                });

            rulesConstructed.push(newRule);
        });

        return rulesConstructed;
    };

    const configurationFormValidate = (values: RetailerOptConfigRich) => {
        const errors: Partial<ConfigurationFormInputs> = {};

        if (!values.program) {
            errors.program = FIELD_REQUIRED_MESSAGE;
        }

        if (!values.retailer) {
            errors.retailer = FIELD_REQUIRED_MESSAGE;
        }

        if (!values.name) {
            errors.name = FIELD_REQUIRED_MESSAGE;
        }

        if (!values.startDateTime) {
            errors.startDateTime = FIELD_REQUIRED_MESSAGE;
        }

        if (!values.endDateTime) {
            errors.endDateTime = FIELD_REQUIRED_MESSAGE;
        } else if (!startDateTimeBeforeEndDateTime(values.startDateTime, values.endDateTime)) {
            errors.endDateTime = ENDDATETIME_FIELD_AFTER_STARTDATE_MESSAGE;
        }

        if (!values.status) {
            errors.status = FIELD_REQUIRED_MESSAGE;
        }

        return errors;
    };

    const configurationFormSubmit = (
        values: RetailerOptConfigRich & { [key: string]: any },
        ruleDefinitions?: ExtendedRuleDefinition[],
        rules?: Rule[],
    ) => {
        values.rules = constructRules(extractRulesFilled(values), ruleDefinitions, rules);

        if (values.id) {
            return ApiService()
                .editConfiguration(values)
                ?.then((_res: any) => {
                    enqueueSnackbar('Configuration was successfully updated.', { variant: 'success' });
                    appHistory.push('/');
                })
                .catch((_res: any) => {
                    enqueueSnackbar('Error occured while updating configuration.', { variant: 'error' });
                });
        } else {
            return ApiService()
                .addConfiguration(values)
                ?.then((_res: any) => {
                    enqueueSnackbar('Configuration was successfully created.', { variant: 'success' });
                    appHistory.push('/');
                })
                .catch((_res: any) => {
                    enqueueSnackbar('Error occured while creating configuration.', { variant: 'error' });
                });
        }
    };

    const getInitialFactValues = (ruleDefinitions: ExtendedRuleDefinition[], rules: Rule[]) => {
        //declare an empty initialRuleValues object
        const initialFactValues: { [key: string]: any } = {};

        if (!ruleDefinitions) {
            return {};
        }

        //loop over fields array
        //if prop does not exist in the initialRuleValues object,
        // pluck off the name and value props and add it to the initiaRulelValues object;
        ruleDefinitions.forEach((ruleDefinition) => {
            Object.keys(ruleDefinition.factDefinitions).forEach((fact) => {
                if (!initialFactValues[generateFieldName(ruleDefinition, ruleDefinition.factDefinitions[fact])]) {
                    initialFactValues[generateFieldName(ruleDefinition, ruleDefinition.factDefinitions[fact])] = rules
                        ?.find((rule) => rule.ruleCode === ruleDefinition.ruleCode)
                        ?.factValues.find(
                            (factValue) => factValue.factCode === ruleDefinition.factDefinitions[fact].factCode,
                            // @ts-ignore
                        )?.value;
                }
            });
        });

        //return initialRuleValues object
        return initialFactValues;
    };

    const generateFactInputsTemplate = (ruleDefinitions: ExtendedRuleDefinition[]) => {
        const factInputs: { [key: string]: any } = {};

        if (!ruleDefinitions) {
            return {};
        }

        //loop loop over fields array
        //if prop does not exit in the initialRuleValues object,
        // pluck off the name and value props and add it to the initiaRulelValues object;
        ruleDefinitions.forEach((ruleDefinition) => {
            Object.keys(ruleDefinition.factDefinitions).forEach((fact) => {
                if (!factInputs[generateFieldName(ruleDefinition, ruleDefinition.factDefinitions[fact])]) {
                    factInputs[generateFieldName(ruleDefinition, ruleDefinition.factDefinitions[fact])] = {};
                }
            });
        });

        return factInputs;
    };

    const formDisabled = (status: RetailerOptConfigStatusEnum) => {
        return status == RetailerOptConfigStatusEnum.DISABLED || !getGlobal().keycloakState.isAuthorized(CAN_EDIT);
    };

    return {
        fetchConfiguration,
        configurationFormValidate,
        configurationFormSubmit,
        getInitialFactValues,
        isEarnOnlyAndNotEarnAndBurnFact,
        formDisabled,
    };
};

export default useConfigurationForm;
